import {
  CloudUploadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  message,
  Progress,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { UploadChangeParam } from 'antd/es/upload/interface';
import * as Component from 'components';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import {
  buildVietnamUploadKey,
  isUploadCancelled,
  uploadFileToVietnam,
} from 'service/api/vietnamUploadService';
import { uploadVideoStore } from 'src/domain/upload/upload-video/store/uploadVideoStore';
import { accountStore } from 'store/accountStore';
import { UploadVideoInterface } from 'type/uploadVideoType';

const { Text, Title } = Typography;
const { Dragger } = Upload;

interface UploadStats {
  totalFiles: number;
  completedFiles: number;
  uploadingFiles: number;
  failedFiles: number;
  normalFiles: number;
  totalSize: number;
  uploadedSize: number;
  avgSpeed: number;
}

// LG Channel Vietnam direct upload: uploads video files only, no automatic content creation.
// 접근 자격은 `is_lg_channel_vietnam` flag + `vietnam_upload.upload` code (constants/permission.ts).
const VietnamAssetAdd: React.FC = () => {
  const [uploading, setUploading] = useState(false);
  // 파일별 진행 중인 업로드의 AbortController (취소 시 파트 PUT 중단 + 서버 abort 호출)
  const abortControllersRef = useRef<Record<string, AbortController>>({});

  // Get file list from uploadVideoStore
  const fileList = uploadVideoStore.getUploadVideoList();

  // Calculate upload statistics
  const getUploadStats = (): UploadStats => {
    const totalFiles = fileList.length;
    const completedFiles = fileList.filter(file => file.state === 'uploaded').length;
    const uploadingFiles = fileList.filter(file => file.state === 'uploading').length;
    const failedFiles = fileList.filter(file => file.state === 'failed').length;
    const normalFiles = fileList.filter(file => file.state === 'normal').length;
    const totalSize = fileList.reduce((acc, file) => acc + (file.size || 0), 0);

    // Calculate uploaded size (based on progress)
    const uploadedSize = fileList.reduce((acc, file) => {
      const fileSize = file.size || 0;
      if (file.state === 'uploaded') {
        return acc + fileSize;
      } else if (file.state === 'uploading') {
        const progress = Math.max(0, Math.min(100, file.percent || 0));
        return acc + (fileSize * progress) / 100;
      }
      return acc;
    }, 0);

    // Calculate average upload speed (average of uploading files)
    const uploadingWithSpeed = fileList.filter(
      file => file.state === 'uploading' && (file.speed || 0) > 0
    );
    const avgSpeed =
      uploadingWithSpeed.length > 0
        ? uploadingWithSpeed.reduce((acc, file) => acc + (file.speed || 0), 0) /
          uploadingWithSpeed.length
        : 0;

    return {
      totalFiles,
      completedFiles,
      uploadingFiles,
      failedFiles,
      normalFiles,
      totalSize,
      uploadedSize,
      avgSpeed,
    };
  };

  // File change handler
  const handleFileChange = (info: UploadChangeParam<any>) => {
    uploadVideoStore.setUploadVideoList(info as UploadChangeParam<UploadVideoInterface>);
  };

  // 업로드 에러 처리: 취소는 normal로 되돌려 재시도 가능, 그 외는 failed + 사유 노출
  const handleUploadError = (videoFile: UploadVideoInterface, error: unknown) => {
    if (isUploadCancelled(error)) {
      videoFile.state = 'normal';
      videoFile.percent = 0;
      videoFile.speed = 0;
      uploadVideoStore.updateUploadVideo(videoFile);
      return;
    }

    console.error('Upload error for file:', videoFile.name, error);
    videoFile.state = 'failed';
    uploadVideoStore.updateUploadVideo(videoFile);

    const response = (error as { response?: { status?: number; data?: any } })?.response;
    const detail =
      response?.data?.detail ||
      response?.data?.error ||
      (error as Error)?.message ||
      'Unknown error';
    if (response?.status === 403) {
      // LG Channel Vietnam 계정 아님 / `vietnam_upload.upload` code 없음 / 미인증
      message.error(
        'You do not have permission to upload. (LG Channel Vietnam account with ' +
          'vietnam_upload.upload permission required.)'
      );
    } else {
      // 400(key 규칙 위반/S3 오류) 및 기타: 서버가 준 상세 사유를 노출
      message.error(`Upload failed: ${detail}`);
    }
  };

  // Execute actual upload (files only — no content creation)
  const handleUpload = async () => {
    const uploadableFiles = fileList.filter(
      file => file.state === 'normal' || file.state === 'failed'
    );

    if (uploadableFiles.length === 0) {
      if (fileList.length === 0) {
        message.warning('Please select files to upload.');
      } else {
        message.info('No new files to upload.');
      }
      return;
    }

    // 업로드 경로는 회사 기준으로 격리된다 (vietnam/{company}/...). company가 없으면 진행 불가.
    const companySlug = accountStore.getAccount()?.company?.slug;
    if (!companySlug) {
      message.error('Cannot determine your company. Please re-login or contact an administrator.');
      return;
    }

    setUploading(true);
    let successCount = 0;
    try {
      await Promise.all(
        uploadableFiles.map(async videoFile => {
          const file = videoFile.originFileObj as File | undefined;
          if (!file) {
            videoFile.state = 'failed';
            uploadVideoStore.updateUploadVideo(videoFile);
            return;
          }

          const controller = new AbortController();
          abortControllersRef.current[videoFile.uid] = controller;

          videoFile.state = 'uploading';
          videoFile.percent = 0;
          videoFile.speed = 0;
          uploadVideoStore.updateUploadVideo(videoFile);

          try {
            const key = buildVietnamUploadKey(companySlug, videoFile.name);
            await uploadFileToVietnam(file, key, {
              signal: controller.signal,
              onProgress: (percent, speed) =>
                uploadVideoStore.updateProgress(videoFile.uid, percent, speed),
            });

            videoFile.state = 'uploaded';
            videoFile.percent = 100;
            uploadVideoStore.updateUploadVideo(videoFile);
            successCount++;
          } catch (error) {
            handleUploadError(videoFile, error);
          } finally {
            delete abortControllersRef.current[videoFile.uid];
          }
        })
      );

      if (successCount > 0) {
        message.success(`${successCount} files uploaded successfully.`);
      }
    } finally {
      setUploading(false);
    }
  };

  // 진행 중인 모든 업로드 취소 → 각 파트 PUT 중단 + 서버 abort 호출
  const handleCancelAll = () => {
    const controllers = Object.values(abortControllersRef.current);
    if (controllers.length === 0) {
      return;
    }
    controllers.forEach(controller => controller.abort());
    message.info('Cancelling upload...');
  };

  // Remove file
  const handleRemoveFile = (file: any) => {
    const videoFile = file as UploadVideoInterface;
    if (videoFile.state === 'uploading') {
      message.warning('Cannot remove file while uploading.');
      return;
    }
    uploadVideoStore.removeUploadVideoList(videoFile.uid);
  };

  // Clear all files
  const handleClearAll = () => {
    const uploadingFiles = fileList.filter(file => file.state === 'uploading');
    if (uploadingFiles.length > 0) {
      message.warning('Cannot clear all while files are uploading.');
      return;
    }
    uploadVideoStore.init();
    message.success('All files cleared.');
  };

  // Retry failed files
  const handleRetryFailed = async () => {
    const failedFiles = fileList.filter(file => file.state === 'failed');
    if (failedFiles.length === 0) {
      message.info('No files to retry.');
      return;
    }

    // Change failed files' state to normal
    failedFiles.forEach(file => {
      file.state = 'normal';
      file.percent = 0;
      file.speed = 0;
      uploadVideoStore.updateUploadVideo(file);
    });

    message.info(`${failedFiles.length} files added to retry queue.`);
  };

  // Determine color by upload state
  const getStateColor = (state: string) => {
    switch (state) {
      case 'uploaded':
        return '#52c41a';
      case 'uploading':
        return '#1890ff';
      case 'failed':
        return '#ff4d4f';
      default:
        return '#d9d9d9';
    }
  };

  // Get text by upload state
  const getStateText = (state: string) => {
    switch (state) {
      case 'uploaded':
        return 'Complete';
      case 'uploading':
        return 'Uploading';
      case 'failed':
        return 'Failed';
      default:
        return 'Waiting';
    }
  };

  // Convert speed to readable format
  const formatSpeed = (bytesPerSecond: number): string => {
    if (bytesPerSecond === 0) return '0 B/s';

    const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    const k = 1024;
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));

    return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(1)) + ' ' + units[i];
  };

  // Convert file size to readable format
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + units[i];
  };

  const stats = getUploadStats();
  const overallProgress =
    stats.totalSize > 0 ? Math.round((stats.uploadedSize / stats.totalSize) * 100) : 0;

  // Handle upload completion
  useEffect(() => {
    // Process only when upload is finished and files exist
    if (fileList.length > 0 && !uploading) {
      const allCompleted = fileList.every(
        file => file.state === 'uploaded' || file.state === 'failed'
      );

      if (allCompleted) {
        const allSucceeded = fileList.every(file => file.state === 'uploaded');

        if (allSucceeded) {
          const timer = setTimeout(() => {
            uploadVideoStore.init();
            message.success('All files uploaded successfully.');
          }, 1000);

          return () => clearTimeout(timer);
        }
        // When there are failed files → stay on current page (do nothing)
      }
    }
  }, [fileList, uploading]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clear list when leaving page
      uploadVideoStore.init();
    };
  }, []);

  return (
    <Component.BasePageComponent>
      <div
        style={{
          padding: '32px',
          backgroundColor: '#f5f7fa',
          minHeight: '100vh',
        }}
      >
        {/* Header */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: '32px',
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          <CloudUploadOutlined
            style={{
              fontSize: '48px',
              color: '#1890ff',
              marginBottom: '16px',
            }}
          />
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Video Upload
          </Title>
          <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginTop: '8px' }}>
            Upload video files and monitor progress in real-time
          </Text>
        </div>

        <Row gutter={[24, 24]}>
          {/* Upload area */}
          <Col xs={24} lg={16}>
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileOutlined style={{ color: '#1890ff' }} />
                  <span>File Upload</span>
                </div>
              }
              extra={
                fileList.length > 0 && (
                  <Space>
                    {stats.failedFiles > 0 && (
                      <Button
                        type="text"
                        icon={<PlayCircleOutlined />}
                        onClick={handleRetryFailed}
                        disabled={uploading}
                      >
                        Retry
                      </Button>
                    )}
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleClearAll}
                      disabled={uploading}
                    >
                      Clear All
                    </Button>
                  </Space>
                )
              }
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: 'none',
              }}
            >
              {/* Drag and drop upload */}
              <Dragger
                multiple
                accept="video/*"
                fileList={fileList as any[]}
                onChange={handleFileChange}
                beforeUpload={() => false}
                onRemove={handleRemoveFile}
                disabled={uploading}
                style={{
                  padding: '40px',
                  backgroundColor: uploading ? '#f0f0f0' : '#fafafa',
                  border: '2px dashed #d9d9d9',
                  borderRadius: '12px',
                }}
              >
                <p className="ant-upload-drag-icon">
                  <CloudUploadOutlined
                    style={{
                      fontSize: '64px',
                      color: uploading ? '#d9d9d9' : '#1890ff',
                    }}
                  />
                </p>
                <p
                  className="ant-upload-text"
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: uploading ? '#999' : 'inherit',
                  }}
                >
                  {uploading ? 'Uploading...' : 'Drag files here or click to select'}
                </p>
                <p className="ant-upload-hint" style={{ fontSize: '14px', color: '#999' }}>
                  Multiple files can be uploaded simultaneously.
                </p>
              </Dragger>

              {/* Upload button */}
              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <Space size="middle">
                  <Button
                    type="primary"
                    size="large"
                    icon={<CloudUploadOutlined />}
                    onClick={handleUpload}
                    loading={uploading}
                    disabled={stats.normalFiles + stats.failedFiles === 0}
                    style={{
                      minWidth: '200px',
                      height: '48px',
                      fontSize: '16px',
                      borderRadius: '8px',
                    }}
                  >
                    {uploading
                      ? 'Uploading...'
                      : `Upload ${stats.normalFiles + stats.failedFiles} Files`}
                  </Button>
                  {fileList.length > 0 && !uploading && (
                    <Button
                      size="large"
                      onClick={handleClearAll}
                      style={{
                        height: '48px',
                        borderRadius: '8px',
                      }}
                    >
                      Clear All
                    </Button>
                  )}
                  {uploading && (
                    <Button
                      size="large"
                      danger
                      onClick={handleCancelAll}
                      style={{
                        height: '48px',
                        borderRadius: '8px',
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </Space>
              </div>
            </Card>
          </Col>

          {/* Upload status and information */}
          <Col xs={24} lg={8}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Overall upload progress */}
              {stats.totalFiles > 0 && (
                <Card
                  title="Overall Upload Progress"
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: 'none',
                  }}
                >
                  <Progress
                    percent={overallProgress}
                    status={
                      stats.failedFiles > 0
                        ? 'exception'
                        : overallProgress === 100
                          ? 'success'
                          : stats.uploadingFiles > 0
                            ? 'active'
                            : 'normal'
                    }
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068',
                    }}
                    strokeWidth={8}
                  />
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title="Upload Speed"
                          value={formatSpeed(stats.avgSpeed)}
                          valueStyle={{
                            fontSize: '14px',
                            color: stats.avgSpeed > 0 ? '#1890ff' : '#999',
                          }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Completion Rate"
                          value={`${stats.completedFiles}/${stats.totalFiles}`}
                          valueStyle={{ fontSize: '14px' }}
                        />
                      </Col>
                    </Row>
                  </div>
                  {stats.uploadingFiles > 0 && (
                    <Alert
                      message={`Uploading ${stats.uploadingFiles} files...`}
                      type="info"
                      showIcon
                      style={{ marginTop: '12px' }}
                    />
                  )}
                  {stats.failedFiles > 0 && (
                    <Alert
                      message={`${stats.failedFiles} files failed to upload`}
                      description="Click retry button to upload again"
                      type="error"
                      showIcon
                      style={{ marginTop: '12px' }}
                      action={
                        <Button size="small" danger onClick={handleRetryFailed}>
                          Retry
                        </Button>
                      }
                    />
                  )}
                </Card>
              )}

              {/* Upload statistics */}
              <Card
                title="Upload Status"
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: 'none',
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Total Files</Text>
                    <Text strong>{stats.totalFiles}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Completed Files</Text>
                    <Text strong style={{ color: '#52c41a' }}>
                      {stats.completedFiles}
                    </Text>
                  </div>
                  {stats.uploadingFiles > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Uploading</Text>
                      <Text strong style={{ color: '#1890ff' }}>
                        {stats.uploadingFiles}
                      </Text>
                    </div>
                  )}
                  {stats.normalFiles > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Waiting</Text>
                      <Text strong style={{ color: '#d9d9d9' }}>
                        {stats.normalFiles}
                      </Text>
                    </div>
                  )}
                  {stats.failedFiles > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Failed</Text>
                      <Text strong style={{ color: '#ff4d4f' }}>
                        {stats.failedFiles}
                      </Text>
                    </div>
                  )}
                  <Divider style={{ margin: '12px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Total Size</Text>
                    <Text strong>{formatFileSize(stats.totalSize)}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Uploaded</Text>
                    <Text strong>{formatFileSize(stats.uploadedSize)}</Text>
                  </div>
                </Space>
              </Card>

              {/* 확장자 목록·최대 용량 안내는 실제 제약이 아니라 오해를 만들어 제거했다 */}
              <Card
                title="Recommended Settings"
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: 'none',
                }}
              >
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Recommended resolution: 1080p or higher
                  <br />
                  Recommended bitrate: 8Mbps or higher
                </Text>
              </Card>
            </Space>
          </Col>
        </Row>

        {/* Selected file list */}
        {fileList.length > 0 && (
          <Card
            title={
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span>Upload File List ({fileList.length} files)</span>
                <Space>
                  {stats.avgSpeed > 0 && (
                    <Text type="secondary">Average Speed: {formatSpeed(stats.avgSpeed)}</Text>
                  )}
                </Space>
              </div>
            }
            style={{
              marginTop: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: 'none',
            }}
          >
            <Row gutter={[16, 16]}>
              {fileList.map(file => (
                <Col xs={24} sm={12} md={8} lg={6} key={file.uid}>
                  <Card
                    size="small"
                    style={{
                      textAlign: 'center',
                      borderRadius: '8px',
                      border: `2px solid ${getStateColor(file.state)}`,
                      backgroundColor:
                        file.state === 'failed'
                          ? '#fff2f0'
                          : file.state === 'uploaded'
                            ? '#f6ffed'
                            : 'white',
                    }}
                    actions={[
                      <Button
                        key={file.uid}
                        type="text"
                        danger
                        size="small"
                        onClick={() => handleRemoveFile(file)}
                        disabled={file.state === 'uploading'}
                        icon={<DeleteOutlined />}
                      >
                        Remove
                      </Button>,
                    ]}
                  >
                    <div style={{ padding: '12px 0' }}>
                      <div style={{ position: 'relative', marginBottom: '8px' }}>
                        <FileOutlined
                          style={{
                            fontSize: '32px',
                            color: getStateColor(file.state),
                          }}
                        />
                        {/* Status badge */}
                        <Tag
                          color={getStateColor(file.state)}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '20px',
                            fontSize: '10px',
                            lineHeight: '16px',
                            padding: '0 6px',
                          }}
                        >
                          {getStateText(file.state)}
                        </Tag>
                      </div>

                      <div
                        style={{
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '12px',
                        }}
                        title={file.name}
                      >
                        {file.name}
                      </div>

                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '8px' }}>
                        {formatFileSize(file.size || 0)}
                      </div>

                      {/* Individual file progress */}
                      {(file.state === 'uploading' || file.state === 'uploaded') && (
                        <div style={{ marginTop: '8px' }}>
                          <Progress
                            percent={Math.round(file.percent || 0)}
                            size="small"
                            status={file.state === 'uploaded' ? 'success' : 'active'}
                            style={{
                              width: '70%',
                            }}
                            strokeColor={getStateColor(file.state)}
                            showInfo={false}
                          />
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginTop: '4px',
                            }}
                          >
                            <Text style={{ fontSize: '10px', color: '#999' }}>
                              {Math.round(file.percent || 0)}%
                            </Text>
                            {file.state === 'uploading' && file.speed > 0 && (
                              <Text style={{ fontSize: '10px', color: '#999' }}>
                                {formatSpeed(file.speed)}
                              </Text>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Show error for failed files */}
                      {file.state === 'failed' && (
                        <div style={{ marginTop: '8px' }}>
                          <ExclamationCircleOutlined
                            style={{ color: '#ff4d4f', marginRight: '4px' }}
                          />
                          <Text type="danger" style={{ fontSize: '10px' }}>
                            Upload Failed
                          </Text>
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        )}
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(VietnamAssetAdd);
